import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@mlm/ui/lib/cn';
import { COACHING_VISION_BOARD_LIMIT } from '@mlm/shared/coaching';
import {
  Block,
  CoachButton,
  ErrorNote,
  Field,
  inputClass,
  Loading,
  Screen,
} from '../components/primitives';
import { ConfirmDialog } from '../components/confirm-dialog';
import { coaching, type Goal, type VisionBoardItem } from '../lib/api';
import { prepareVisionImage } from '../lib/image';
import { qk, useActiveGoals, useRefreshCoaching } from '../lib/queries';

/** SC-22: the pictures of the life the member is building, each tied to a goal if they like. */
export function VisionBoardPage() {
  const board = useQuery({
    queryKey: qk.visionBoard,
    queryFn: coaching.visionBoard,
    // Picture URLs are signed for an hour: refetching on every focus would only
    // make the browser download the same images again under new URLs.
    staleTime: 20 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const goals = useActiveGoals();
  if (board.isPending) return <Loading />;
  if (board.isError) return <ErrorNote error={board.error} retry={() => void board.refetch()} />;
  const { items, limit } = board.data;
  return (
    <Screen
      wide
      back="/manifest"
      eyebrow="Vision board"
      title="My vision board"
      subtitle="Your goals become more vivid when you can see them."
    >
      <AddPicture count={items.length} limit={limit} goals={goals.data ?? []} />
      {items.length === 0 ? (
        <Block>
          Give your future a visual form. Add pictures of the goals, experiences and identity you
          are moving toward.
        </Block>
      ) : (
        <ul className="grid grid-cols-2 gap-x-4 gap-y-8 border-b border-border px-5 py-6 lg:grid-cols-3 lg:gap-x-6">
          {items.map((item, i) => (
            <Tile key={item.id} item={item} items={items} index={i} goals={goals.data ?? []} />
          ))}
        </ul>
      )}
    </Screen>
  );
}

function AddPicture({ count, limit, goals }: { count: number; limit: number; goals: Goal[] }) {
  const refresh = useRefreshCoaching();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [goalId, setGoalId] = useState('');
  const full = count >= (limit || COACHING_VISION_BOARD_LIMIT);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const reset = () => {
    setFile(null);
    setCaption('');
    setGoalId('');
    if (input.current) input.current.value = '';
  };

  const add = useMutation({
    mutationFn: async () => {
      const image = await prepareVisionImage(file!);
      const form = new FormData();
      if (caption.trim()) form.append('caption', caption.trim());
      if (goalId) form.append('goalId', goalId);
      form.append('file', image, image instanceof File ? image.name : 'vision.jpg');
      return coaching.addVisionBoardItem(form);
    },
    meta: { successMessage: 'Added to your vision board.' },
    onSuccess: () => {
      coaching.event('vision_board_updated', { action: 'add' });
      reset();
      void refresh();
    },
  });

  return (
    <section className="border-b border-border px-5 py-6">
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/heic,image/heif"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
      />
      {file && preview ? (
        <div className="space-y-4 lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-8 lg:space-y-0">
          <img
            src={preview}
            alt="The picture you chose"
            className="aspect-[4/5] w-full max-w-[240px] object-cover"
          />
          <div className="space-y-4">
            <Field label="Caption (optional)" htmlFor="vision-caption">
              <input
                id="vision-caption"
                className={inputClass}
                maxLength={200}
                value={caption}
                placeholder="Our first family trip abroad"
                onChange={(e) => setCaption(e.target.value)}
              />
            </Field>
            {goals.length > 0 && (
              <Field label="Goal (optional)" htmlFor="vision-goal">
                <select
                  id="vision-goal"
                  className={cn(inputClass, '[color-scheme:dark]')}
                  value={goalId}
                  onChange={(e) => setGoalId(e.target.value)}
                >
                  <option value="">No goal</option>
                  {goals.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.title}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <div className="flex flex-wrap items-center gap-4">
              <CoachButton kind="gold" disabled={add.isPending} onClick={() => add.mutate()}>
                {add.isPending ? 'Adding' : 'Add to my board'}
              </CoachButton>
              <CoachButton kind="quiet" disabled={add.isPending} onClick={reset}>
                Cancel
              </CoachButton>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="eyebrow">
            {count} of {limit || COACHING_VISION_BOARD_LIMIT} pictures
          </p>
          {full ? (
            <p className="text-sm text-muted-foreground">
              Your board is full. Remove a picture to add another.
            </p>
          ) : (
            <CoachButton kind="gold" onClick={() => input.current?.click()}>
              Add a picture
            </CoachButton>
          )}
        </div>
      )}
    </section>
  );
}

function Tile({
  item,
  items,
  index,
  goals,
}: {
  item: VisionBoardItem;
  items: VisionBoardItem[];
  index: number;
  goals: Goal[];
}) {
  const qc = useQueryClient();
  const refresh = useRefreshCoaching();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [caption, setCaption] = useState(item.caption ?? '');
  const [goalId, setGoalId] = useState(item.goalId ?? '');

  // Keep each picture's current URL: a fresh signature for the same image would
  // make every tile download and repaint just because the order changed.
  const setBoard = (next: VisionBoardItem[]) =>
    qc.setQueryData(
      qk.visionBoard,
      (old: { items: VisionBoardItem[]; limit: number } | undefined) => {
        if (!old) return old;
        const urls = new Map(old.items.map((i) => [i.id, i.imageUrl]));
        return {
          ...old,
          items: next.map((i) => ({ ...i, imageUrl: urls.get(i.id) ?? i.imageUrl })),
        };
      },
    );

  const edit = useMutation({
    mutationFn: () =>
      coaching.updateVisionBoardItem(item.id, {
        caption: caption.trim() || null,
        goalId: goalId || null,
      }),
    meta: { successMessage: 'Saved.' },
    onSuccess: (updated) => {
      setBoard(items.map((i) => (i.id === updated.id ? updated : i)));
      setEditing(false);
    },
  });
  const move = useMutation({
    mutationFn: (to: number) => {
      const ids = items.map((i) => i.id);
      ids.splice(index, 1);
      ids.splice(to, 0, item.id);
      return coaching.reorderVisionBoard(ids);
    },
    onSuccess: (next) => setBoard(next),
  });
  const remove = useMutation({
    mutationFn: () => coaching.deleteVisionBoardItem(item.id),
    meta: { successMessage: 'Removed from your board.' },
    onSuccess: () => {
      // The tile lives until the refetch lands, so close the dialog first.
      setConfirming(false);
      coaching.event('vision_board_updated', { action: 'remove' });
      void refresh();
    },
  });

  const busy = edit.isPending || move.isPending || remove.isPending;
  return (
    <li className="flex min-w-0 flex-col">
      <img
        src={item.imageUrl}
        alt={item.caption ?? 'A picture on your vision board'}
        loading="lazy"
        className="aspect-[4/5] w-full object-cover"
      />
      <div className="flex flex-1 flex-col gap-2 pt-3">
        {editing ? (
          <>
            <label htmlFor={`caption-${item.id}`} className="sr-only">
              Caption
            </label>
            <input
              id={`caption-${item.id}`}
              className={cn(inputClass, 'py-2 text-sm')}
              maxLength={200}
              value={caption}
              placeholder="Caption"
              onChange={(e) => setCaption(e.target.value)}
            />
            {goals.length > 0 && (
              <>
                <label htmlFor={`goal-${item.id}`} className="sr-only">
                  Goal
                </label>
                <select
                  id={`goal-${item.id}`}
                  className={cn(inputClass, 'py-2 text-sm [color-scheme:dark]')}
                  value={goalId}
                  onChange={(e) => setGoalId(e.target.value)}
                >
                  <option value="">No goal</option>
                  {goals.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.title}
                    </option>
                  ))}
                </select>
              </>
            )}
            <div className="flex gap-3">
              <CoachButton kind="outline" disabled={busy} onClick={() => edit.mutate()}>
                Save
              </CoachButton>
              <CoachButton kind="quiet" onClick={() => setEditing(false)}>
                Cancel
              </CoachButton>
            </div>
          </>
        ) : (
          <>
            {item.caption && <p className="text-sm leading-snug text-ink">{item.caption}</p>}
            {item.goalTitle && <p className="eyebrow">{item.goalTitle}</p>}
            <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
              <button
                type="button"
                aria-label="Move earlier"
                disabled={busy || index === 0}
                className="flex h-11 w-9 items-center justify-center text-sage hover:text-ink disabled:opacity-30"
                onClick={() => move.mutate(index - 1)}
              >
                <ArrowLeft className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                aria-label="Move later"
                disabled={busy || index === items.length - 1}
                className="flex h-11 w-9 items-center justify-center text-sage hover:text-ink disabled:opacity-30"
                onClick={() => move.mutate(index + 1)}
              >
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
              <CoachButton kind="quiet" onClick={() => setEditing(true)}>
                Edit
              </CoachButton>
              <CoachButton kind="quiet" disabled={busy} onClick={() => setConfirming(true)}>
                Remove
              </CoachButton>
            </div>
          </>
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        title="Remove this picture?"
        body="It comes off your board. Everything else on the board stays as it is."
        confirmLabel="Remove it"
        busy={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirming(false)}
      />
    </li>
  );
}
